import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { contentCapabilities, type ReviewViewer } from '@/auth/content-capabilities'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { useViewerRole } from '@/auth/useViewerRole'
import { useTenantLocales } from '@/hooks/products/useTenantLocales'
import { contentService } from '@/services/content.service'
import { localeOptions } from './content-locales'
import { contentErrorMessage, contentFieldError, type Translate } from './content-ui'

/** Query keys of the content screens (TO-420 design D3). */
export const contentKeys = {
	all: ['content'] as const,
	types: ['content', 'types'] as const,
	texts: ['content', 'texts'] as const,
	text: (id: string) => ['content', 'text', id] as const,
	versions: (textId: string) => ['content', 'versions', textId] as const,
	localization: (id: string) => ['content', 'localization', id] as const,
	approvals: (id: string) => ['content', 'approvals', id] as const,
	diff: (id: string, against: string) => ['content', 'diff', id, against] as const,
	inbox: ['content', 'inbox'] as const,
	assignments: ['content', 'assignments'] as const,
	allAssignments: ['content', 'assignments', 'all'] as const,
	tenantAssignments: ['content', 'assignments', 'tenant'] as const,
	productTexts: (productId: string) => ['content', 'product-texts', productId] as const,
}

/** The `content` namespace `t`, plus the error helpers bound to it. */
export function useContentT(): {
	t: Translate
	errorMessage: (error: unknown) => string
	fieldError: (error: unknown) => { field: string; message: string }
} {
	const { t, i18n } = useTranslation('content')
	return useMemo(() => {
		const translate: Translate = (key, vars) => t(key, vars ?? {})
		const exists = (key: string) => i18n.exists(key, { ns: 'content' })
		return {
			t: translate,
			errorMessage: (error: unknown) => contentErrorMessage(error, translate, exists),
			fieldError: (error: unknown) => contentFieldError(error, translate, exists),
		}
	}, [t, i18n])
}

/** The viewer as the review rules and capabilities see it. */
export function useContentViewer(): ReviewViewer & { caps: ReturnType<typeof contentCapabilities> } {
	const { permissions, userId } = useViewerPermissions()
	const { role } = useViewerRole()
	return useMemo(
		() => ({ userId, role, permissions, caps: contentCapabilities(permissions) }),
		[userId, role, permissions],
	)
}

/** Every content type (all pages), in API order. */
export const useContentTypes = () =>
	useQuery({ queryKey: contentKeys.types, queryFn: contentService.allTypes, staleTime: 30_000 })

/** Every live assignment (all pages) — "used by", archive gating and pickers share it. */
export const useAllAssignments = (enabled = true) =>
	useQuery({ queryKey: contentKeys.allAssignments, queryFn: () => contentService.allAssignments(), enabled })

/** A text's versions, newest first. */
export const useTextVersions = (textId: string, enabled = true) =>
	useQuery({ queryKey: contentKeys.versions(textId), queryFn: () => contentService.versions(textId), enabled })

/** Language options for pickers: tenant content locales, then the type's required locales (REQ-013). */
export function useContentLocales(typeRequired: readonly string[] = []): { locales: string[]; isLoading: boolean } {
	const { settings, isLoading } = useTenantLocales()
	const locales = useMemo(
		() => localeOptions(settings.enabledLocales, typeRequired),
		[settings.enabledLocales, typeRequired],
	)
	return { locales, isLoading }
}

/** "Now" pinned for the component's lifetime, so time-derived query keys stay stable (design D4). */
export function useNow(): Date {
	const [now] = useState(() => new Date())
	return now
}
